import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import * as api from '../../lib/api';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../contexts/AuthContext';
import { ImmigrationPOAWizard, IMMIGRATION_POA_STEPS, validateStep as validatePOAStep } from './wizards/ImmigrationPOAWizard';
import { StatusBadge } from '../../components/ui/StatusBadge';
import ButtonSpinner from '../../components/ui/ButtonSpinner';

const GENERIC_STEPS = {
  will: ['Personal Info', 'Beneficiaries', 'Executor', 'Review'],
  trust: ['Personal Info', 'Trustees', 'Beneficiaries', 'Review'],
};

const GenericWizard = ({ formData, onChange, currentStep, steps }) => {
  if (currentStep === steps.length - 1) return null;
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium text-brand-navy border-b pb-2 mb-4">{steps[currentStep]}</h3>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Full Legal Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-900 outline-none"
          value={formData.fullName || ''}
          onChange={(e) => onChange({ ...formData, fullName: e.target.value })}
          placeholder="Jane Doe"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
        <textarea
          className="w-full px-4 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-slate-900 outline-none min-h-[100px] resize-none"
          value={formData.notes || ''}
          onChange={(e) => onChange({ ...formData, notes: e.target.value })}
          placeholder="Enter details relevant to this section..."
        />
      </div>
    </div>
  );
};

export const DocumentWizard = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, role } = useAuth();
  const [doc, setDoc] = useState(null);
  const [formData, setFormData] = useState({});
  const [currentStep, setCurrentStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [autoSaveTimer, setAutoSaveTimer] = useState(null);
  const errorPanelRef = useRef(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => { loadDoc(); }, [id]);

  const loadDoc = async () => {
    setLoadError(null);
    try {
      const d = await api.getDocumentById(id);
      if (!d) {
        setLoadError('This document could not be found.');
        return;
      }
      setDoc(d);
      setFormData(d.form_data || {});
      setCurrentStep(d.form_data?.currentStep || 0);
    } catch (err) {
      console.error(err);
      setLoadError('Could not load this document. Please go back and try again.');
      setDoc(null);
    }
  };

  const getSteps = (docType) => {
    if (docType === 'immigration_poa') return IMMIGRATION_POA_STEPS;
    return GENERIC_STEPS[docType] || ['Step 1', 'Review'];
  };

  const runValidation = useCallback((step) => {
    if (doc?.type === 'immigration_poa') return validatePOAStep(step, formData);
    return {};
  }, [doc?.type, formData]);

  useEffect(() => {
    if (!doc || doc.status !== 'draft') return;
    if (autoSaveTimer) clearTimeout(autoSaveTimer);
    const timer = setTimeout(async () => {
      try {
        await api.updateDocumentFormData(id, { ...formData, currentStep });
      } catch (err) {
        console.error(err);
      }
    }, 1500);
    setAutoSaveTimer(timer);
    return () => clearTimeout(timer);
  }, [formData]);

  useEffect(() => {
    if (Object.keys(errors).length > 0 && errorPanelRef.current) {
      errorPanelRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [errors]);

  const handleNext = async () => {
    const stepErrors = runValidation(currentStep);
    if (Object.keys(stepErrors).length > 0) { setErrors(stepErrors); return; }
    setErrors({});
    setSaving(true);
    const nextStep = currentStep + 1;
    const updatedData = { ...formData, currentStep: nextStep };
    try {
      await api.updateDocumentFormData(id, updatedData);
    } catch (err) {
      console.error(err);
      setSaving(false);
      return;
    }
    setFormData(updatedData);
    setCurrentStep(nextStep);
    setSaving(false);
  };

  const handleBack = () => { setErrors({}); setCurrentStep((s) => Math.max(0, s - 1)); };

  const handleSubmitReview = async () => {
    setSubmitting(true);
    try {
      await api.updateDocumentFormData(id, formData);
      await api.submitDocumentForReview(id);
      navigate(role === 'client' ? '/client/dashboard' : '/advisor/dashboard');
    } catch (err) {
      console.error(err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loadError) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 rounded-lg p-4">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{loadError}</p>
        </div>
      </div>
    );
  }

  if (!doc) return (
    <div className="flex items-center justify-center py-20">
      <div className="text-center">
        <div className="w-8 h-8 border-2 border-slate-900 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
        <p className="text-slate-500">Loading document...</p>
      </div>
    </div>
  );

  const steps = getSteps(doc.type);
  const isLastStep = currentStep === steps.length - 1;
  const isReadOnly = doc.status !== 'draft';
  const docTypeLabels = {
    will: 'Last Will & Testament',
    trust: 'Revocable Living Trust',
    immigration_poa: 'Special Power of Attorney for Immigration',
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="mb-6">
        <button onClick={() => navigate(-1)} className="text-slate-500 hover:text-brand-navy flex items-center gap-2 mb-4 text-sm transition-colors">
          <SafeIcon icon={FiIcons.FiArrowLeft} /> Back
        </button>
        <div className="flex flex-wrap justify-between items-start gap-3">
          <div>
            <h1 className="page-title">{docTypeLabels[doc.type]}</h1>
            <p className="text-sm text-slate-500 mt-0.5">Document ID: {doc.id.slice(0, 12)}...</p>
          </div>
          <div className="flex items-center gap-3">
            <StatusBadge status={doc.status} />
            {saving && (
              <span className="text-xs text-slate-400 flex items-center gap-1 animate-pulse">
                <div className="w-3 h-3 border border-slate-400 border-t-transparent rounded-full animate-spin" />
                Saving...
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Wizard step labels — stack vertically on mobile */}
      <div className="mb-8">
        <div className="wizard-steps mb-2">
          {steps.map((step, idx) => (
            <div
              key={idx}
              className={`wizard-step-label ${
                idx < currentStep ? 'text-emerald-600' : idx === currentStep ? 'text-brand-navy font-bold' : 'text-slate-400'
              }`}
            >
              {idx + 1}. {step}
            </div>
          ))}
        </div>
        <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-brand-gold transition-all duration-500 ease-out"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
        <p className="text-xs text-slate-400 mt-1 text-right">Step {currentStep + 1} of {steps.length}</p>
      </div>

      {isReadOnly && (
        <div className="mb-4 bg-amber-50 text-amber-800 p-4 rounded-lg border border-amber-200 flex gap-2">
          <SafeIcon icon={FiIcons.FiLock} className="flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Document is {doc.status.replace('_', ' ')}</p>
            <p className="text-xs mt-0.5">This document is currently locked for editing. Contact your advisor for changes.</p>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 md:p-8">
        {doc.type === 'immigration_poa' ? (
          <ImmigrationPOAWizard formData={formData} onChange={isReadOnly ? () => {} : setFormData} currentStep={currentStep} errors={errors} />
        ) : isLastStep ? (
          <div className="text-center py-6">
            <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4">
              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-3xl text-emerald-500" />
            </div>
            <h3 className="text-xl font-bold text-brand-navy mb-2">Ready to Submit</h3>
            <p className="text-slate-500 mb-2">Please review your information before submitting to your advisor.</p>
          </div>
        ) : (
          <GenericWizard formData={formData} onChange={isReadOnly ? () => {} : setFormData} currentStep={currentStep} steps={steps} />
        )}

        {!isReadOnly && (
          <div className="mt-8 flex justify-between items-center pt-6 border-t border-slate-100">
            <button onClick={handleBack} disabled={currentStep === 0 || saving || submitting} className="btn-secondary">
              <SafeIcon icon={FiIcons.FiChevronLeft} /> Previous
            </button>
            {isLastStep ? (
              <button onClick={handleSubmitReview} disabled={submitting || saving} className="btn-primary !bg-emerald-600 hover:!bg-emerald-700">
                {submitting ? (<><ButtonSpinner /> Submitting...</>) : (<><SafeIcon icon={FiIcons.FiSend} /> Submit for Review</>)}
              </button>
            ) : (
              <button onClick={handleNext} disabled={saving || submitting} className="btn-primary">
                {saving ? (<><ButtonSpinner /> Saving...</>) : (<>Save & Continue <SafeIcon icon={FiIcons.FiChevronRight} /></>)}
              </button>
            )}
          </div>
        )}
      </div>

      {Object.keys(errors).length > 0 && (
        <div ref={errorPanelRef} className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex gap-3">
          <SafeIcon icon={FiIcons.FiAlertCircle} className="text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-semibold text-red-700">Please fix the following before continuing:</p>
            <ul className="mt-1 space-y-0.5">
              {Object.values(errors).map((err, i) => (
                <li key={i} className="text-xs text-red-600">• {err}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};